<template>
	<button
		class="voice-button"
		:class="{
			'voice-button--selected': props.isSelected,
			'voice-button--selected-background': props.isSelected && !showProgress,
		}"
		:aria-selected="props.isSelected"
		:aria-label="props.name"
	>
		<img
			:alt="`${props.name}`"
			class="voice-button__image"
			:src="props.src"
		/>

		<div
			v-if="showProgress"
			role="progressbar"
			class="voice-button__progress"
		/>
	</button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
	defineProps<{
		src: string;
		name: string;
		isSelected?: boolean;
		hasProgress?: boolean;
		progress?: number;
	}>(),
	{
		isSelected: false,
		hasProgress: false,
		progress: 0,
	},
);

const showProgress = computed(
	() => props.hasProgress && props.progress !== 100,
);
const progress = computed(() => (props.progress ? `${props.progress}%` : '0'));
</script>

<style scoped lang="scss">
.voice-button {
	$base: &;

	--voice-icon-size: 86px;

	cursor: pointer;

	position: relative;

	overflow: hidden;
	display: block;

	width: var(--voice-icon-size);
	height: var(--voice-icon-size);
	margin: 0 auto;
	padding: 0;

	background-color: var(--hy-color-surface-neutral-deep);
	background-repeat: no-repeat;
	background-position: center;
	border: none;
	border-radius: var(--hy-radius-circle);
	box-shadow:
		0 -4px 0 0 var(--hy-color-alpha-shadow-1000-20) inset,
		0 -5px 0 0 var(--hy-color-alpha-highlight-0-20) inset,
		0 1px 0 0 var(--hy-color-alpha-highlight-0-80) inset,
		0 2px 0 1px var(--hy-color-alpha-highlight-0-20) inset,
		-1px 1px 0 0 var(--hy-color-alpha-shadow-1000-20);

	transition:
		box-shadow 0.1s linear,
		background-color 0.1s linear;

	&::before {
		content: '';

		position: absolute;
		inset: 0;

		opacity: 0;
		background: linear-gradient(
			135deg,
			var(--hy-color-alpha-highlight-0-0) 19.15%,
			var(--hy-color-alpha-highlight-0-20) 28.08%,
			var(--hy-color-alpha-highlight-0-0) 46.98%,
			var(--hy-color-alpha-highlight-0-0) 46.99%,
			var(--hy-color-alpha-highlight-0-0) 56.27%,
			var(--hy-color-alpha-highlight-0-20) 85.54%
		);

		transition: opacity 0.1s linear;
	}

	&::after {
		content: '';

		position: absolute;
		inset: 0;

		width: var(--voice-icon-size);
		height: var(--voice-icon-size);

		background-color: transparent;
		border-radius: var(--hy-radius-circle);
		box-shadow:
			0 -4px 0 0 var(--hy-color-alpha-shadow-1000-20) inset,
			0 -5px 0 0 var(--hy-color-alpha-highlight-0-20) inset,
			0 1px 0 0 var(--hy-color-alpha-highlight-0-80) inset,
			0 2px 0 1px var(--hy-color-alpha-highlight-0-20) inset,
			-1px 1px 0 0 var(--hy-color-alpha-shadow-1000-20);

		transition: box-shadow 0.1s linear;
	}

	&__progress {
		will-change: width;

		position: absolute;
		top: 0;
		left: 0;

		width: v-bind(progress);
		height: 100%;

		opacity: 0.5;
		background-color: var(--hy-color-surface-brand-default);

		transition: width 0.25s linear;
	}

	&:active {
		box-shadow:
			0 0 0 0 var(--hy-color-border-brand),
			0 0 0 0 var(--hy-color-alpha-shadow-1000-20) inset,
			0 0 0 0 var(--hy-color-alpha-highlight-0-20) inset,
			0 0 0 0 var(--hy-color-alpha-highlight-0-100) inset,
			0 0 0 0 var(--hy-color-alpha-shadow-1000-20);

		&::before {
			opacity: 0;
		}

		&::after {
			box-shadow:
				0 -3px 3px 0 var(--hy-color-alpha-shadow-1000-20) inset,
				0 5px 4px 0 var(--hy-color-alpha-shadow-1000-60) inset;
		}

		#{$base}__image {
			transform: translateY(4%);
		}
	}

	&:hover {
		&::before {
			opacity: 1;
		}
	}

	&__image {
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}

	&--selected {
		border: 2px solid var(--hy-color-surface-brand-default);
		box-shadow:
			0 0 17.684px 0 var(--hy-color-border-brand),
			0 -4.661px 0 0 var(--hy-color-alpha-shadow-1000-20) inset,
			0 -6.063px 1.516px 0 var(--hy-color-alpha-highlight-0-20) inset,
			0 1.628px 2.274px 0 var(--hy-color-alpha-highlight-0-100) inset,
			-1.83px 1.83px 3.659px 0 var(--hy-color-alpha-shadow-1000-20);
		transition:
			box-shadow 0.1s linear,
			background-color 0.1s linear;

		&:hover {
			&::before {
				opacity: 1;
			}
		}

		&:active {
			background-color: var(--hy-color-surface-brand-secondary);
			box-shadow:
				0 0 15.158px 0 var(--hy-color-border-brand),
				0 5.053px 5.053px 0 var(--hy-color-alpha-shadow-1000-20);

			&::before {
				opacity: 0;
			}
		}
	}

	&--selected-background {
		background-color: var(--hy-color-surface-brand-default);
	}
}
</style>
